import React, { JSX, useLayoutEffect, useRef } from 'react'
import {
  MERCH_PRICE_DOWN_COLOR,
  MERCH_PRICE_FLAT_COLOR,
  MERCH_PRICE_UP_COLOR,
  MERCH_BASE_FONT_STACK,
  MERCH_STYLE_DEFAULTS,
  ResolvedMerchStyle,
  merchFontStack,
  merchFontWeight
} from 'lib/merchStyle'

export const MERCH_BOARD_NATURAL_WIDTH = 610

export const MERCH_TITLE_BASE_SHADOW = '0 0 14px rgba(255, 255, 255, 0.25)'

export interface MerchRowVariant {
  id: string
  label: string
  linkUrl?: string | null
}

export interface MerchBoardPanelRow {
  productId: string
  title: string
  priceCents: number | null
  imageUrl: string | null
  linkUrl: string | null
  trend: string | null
  variants: MerchRowVariant[]
  pctLabel?: string | null
}

const money = (cents: number): string => (cents / 100).toFixed(2)

const initials = (title: string): string =>
  title
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 3)

const quoteClass = (trend: string | null): string => {
  if (trend === 'up') return 'quote up'
  if (trend === 'down') return 'quote down'
  return 'quote'
}

const httpsUrlOrNull = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string' || value === '') return null
  try {
    if (new URL(value).protocol !== 'https:') return null
  } catch {
    return null
  }
  return value
}

const cssBgUrlOrNull = (value: string | null | undefined): string | null => {
  const url = httpsUrlOrNull(value)
  if (url === null) return null
  return /^[\x21-\x7e]+$/.test(url) && !/["'()\\]/.test(url) ? url : null
}

export const VARIANT_BOX_BASE_FS_PX = 20
export const VARIANT_BOX_BORDER_PX = 2
export const VARIANT_FIT_FLOOR = 0.45
const VARIANT_FIT_SLACK_PX = 1
const VARIANT_FIT_RETRIES = 4

const stripWraps = (strip: HTMLElement): boolean => {
  let top: number | null = null
  for (const box of strip.querySelectorAll<HTMLElement>('.variant-box')) {
    const t = box.getBoundingClientRect().top
    if (top === null) top = t
    else if (Math.abs(t - top) > 0.5) return true
  }
  return false
}

export const fitVariantStrips = (root: HTMLElement): void => {
  const strips = Array.from(root.querySelectorAll<HTMLElement>('.row.has-variants .name-wrap > .variants'))
  if (strips.length === 0) return
  for (const strip of strips) {
    strip.style.removeProperty('--variant-fs')
    strip.style.flexWrap = 'nowrap'
    strip.style.width = 'max-content'
  }
  const ks = strips.map((strip) => {
    const cell = strip.parentElement
    const rowMain = cell?.parentElement
    if (cell == null || rowMain == null) return 1
    const natural = strip.getBoundingClientRect().width
    const avail = cell.getBoundingClientRect().width
    if (natural <= 0 || avail <= 0) return 1
    if (natural <= avail) return 1
    const s = rowMain.offsetWidth > 0 ? rowMain.getBoundingClientRect().width / rowMain.offsetWidth : 1
    const boxes = strip.querySelectorAll('.variant-box').length
    const fixed = (boxes * 2 * VARIANT_BOX_BORDER_PX + VARIANT_FIT_SLACK_PX) * s
    const scalable = natural - boxes * 2 * VARIANT_BOX_BORDER_PX * s
    if (scalable <= 0) return 1
    return Math.min(1, (avail - fixed) / scalable)
  })
  const applied = strips.map((strip, i) => {
    strip.style.flexWrap = ''
    strip.style.width = ''
    const k = Math.max(VARIANT_FIT_FLOOR, ks[i])
    if (k < 1) strip.style.setProperty('--variant-fs', `${VARIANT_BOX_BASE_FS_PX * k}px`)
    else if (strip.getAttribute('style') === '') strip.removeAttribute('style')
    return k
  })
  for (let pass = 0; pass < VARIANT_FIT_RETRIES; pass++) {
    let again = false
    strips.forEach((strip, i) => {
      if (applied[i] <= VARIANT_FIT_FLOOR || !stripWraps(strip)) return
      applied[i] = Math.max(VARIANT_FIT_FLOOR, applied[i] * 0.99)
      strip.style.setProperty('--variant-fs', `${VARIANT_BOX_BASE_FS_PX * applied[i]}px`)
      again = true
    })
    if (!again) break
  }
}

const TICKER_CSS = `
.merch-overlay { position: relative; width: 100vw; height: 100vh; overflow: hidden; color: #f4f4f4; font-family: ${MERCH_BASE_FONT_STACK}; --row-h: 85px; --tile-w: 55px; --row-border-w: 1px; --tile-square: calc(var(--row-h) - 2 * var(--row-border-w)); --square-delta: 0px; --pad-delta: calc(2 * (var(--merch-outer-pad-x, 30px) - 30px) + 2 * (var(--merch-board-pad-x, 30px) - 30px)); }
.merch-overlay.square-photos { --square-delta: calc(var(--tile-square) - var(--tile-w)); }
.merch-overlay.photo-height { --row-h: calc(var(--merch-photo-h, 83px) + 2 * var(--row-border-w)); }
.merch-overlay .ticker { position: absolute; top: 24px; right: 24px; width: calc(var(--merch-wrap-width, ${MERCH_BOARD_NATURAL_WIDTH}px) + var(--square-delta, 0px) + var(--pad-delta, 0px) + 2 * var(--merch-outer-border-w, 0px)); display: flex; flex-direction: column; background: var(--merch-outer-bg); border: var(--merch-outer-border-w, 0px) solid var(--merch-outer-border-color, #ffffff); border-radius: var(--merch-outer-radius); padding: var(--merch-outer-pad-top, 45px) var(--merch-outer-pad-x, 30px) var(--merch-outer-pad-bottom, 30px); }
.merch-overlay .store-title { font-weight: var(--merch-title-weight, 900); font-size: 40.5px; line-height: 1; letter-spacing: 0.14em; text-align: center; color: var(--merch-title-color); font-family: var(--merch-title-font); text-shadow: var(--merch-title-glow, ${MERCH_TITLE_BASE_SHADOW}); margin: 0 0 49.5px; white-space: nowrap; }
.merch-overlay .board { background: var(--merch-board-bg); border-radius: var(--merch-board-radius); padding: 30px var(--merch-board-pad-x, 30px); display: flex; flex-direction: column; gap: 20px; overflow: hidden; box-shadow: 0 8px 40px rgba(0, 0, 0, 0.55); }
.merch-overlay.board-bg-image .board { background-image: var(--merch-board-bg-image); background-size: cover; background-position: center; }
.merch-overlay .row { display: flex; align-items: stretch; gap: 0; text-decoration: none; color: inherit; background: var(--merch-row-bg); border: var(--row-border-w) solid var(--merch-row-border, #2c2c2c); min-height: var(--row-h); transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1); will-change: transform; }
.merch-overlay .row.flash-up { animation: merch-flash-up 0.9s ease-out; }
.merch-overlay .row.flash-down { animation: merch-flash-down 0.9s ease-out; }
@keyframes merch-flash-up { 0% { background: rgba(61, 220, 90, 0.28); } 100% { background: var(--merch-row-bg); } }
@keyframes merch-flash-down { 0% { background: rgba(255, 75, 71, 0.28); } 100% { background: var(--merch-row-bg); } }
.merch-overlay .row.blink .quote { animation: merch-blink 0.4s steps(1, end) 3; }
@keyframes merch-blink { 0% { visibility: hidden; } 50% { visibility: visible; } 100% { visibility: visible; } }
.merch-overlay .tile { flex: 0 0 var(--tile-w); width: var(--tile-w); align-self: stretch; background: var(--merch-tile-bg); display: flex; align-items: center; justify-content: center; overflow: hidden; }
.merch-overlay .tile img { width: 100%; height: 100%; object-fit: contain; }
.merch-overlay.square-photos .tile { align-self: center; flex-basis: var(--tile-square); width: var(--tile-square); height: var(--tile-square); }
.merch-overlay .tile .tile-initials { font-weight: 900; font-size: 26px; color: #141414; letter-spacing: 0.02em; }
.merch-overlay .row-main { flex: 1 1 auto; display: flex; align-items: flex-start; justify-content: space-between; gap: 15px; padding: 7px 25px 6px; min-width: 0; }
.merch-overlay .name { font-weight: var(--merch-name-weight, 700); font-size: 27px; line-height: 35px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--merch-name-color); font-family: var(--merch-name-font); overflow-wrap: anywhere; text-shadow: var(--merch-text-glow, none); }
.merch-overlay .quote { flex: 0 0 auto; align-self: flex-start; display: flex; align-items: baseline; gap: 6px; margin: 4px -5px 0 0; font-weight: var(--merch-price-weight, 800); font-size: 27px; line-height: 1; letter-spacing: 0.06em; font-family: var(--merch-price-font); color: ${MERCH_PRICE_FLAT_COLOR}; text-shadow: var(--merch-text-glow, none); }
.merch-overlay .quote.up { color: ${MERCH_PRICE_UP_COLOR}; }
.merch-overlay .quote.down { color: ${MERCH_PRICE_DOWN_COLOR}; }
.merch-overlay .arrow { display: inline-block; width: 17px; height: 15px; transform: translateY(1.5px); --dot: currentColor 1.43px, transparent 2.18px; background-image: radial-gradient(circle at 8.5px 3.25px, var(--dot)), radial-gradient(circle at 5.3125px 7.5px, var(--dot)), radial-gradient(circle at 8.5px 7.5px, var(--dot)), radial-gradient(circle at 11.6875px 7.5px, var(--dot)), radial-gradient(circle at 2.125px 11.75px, var(--dot)), radial-gradient(circle at 5.3125px 11.75px, var(--dot)), radial-gradient(circle at 8.5px 11.75px, var(--dot)), radial-gradient(circle at 11.6875px 11.75px, var(--dot)), radial-gradient(circle at 14.875px 11.75px, var(--dot)); filter: var(--merch-arrow-glow, none); }
.merch-overlay .quote.down .arrow { transform: translateY(1.5px) rotate(180deg); }
.merch-overlay.vcr-price .arrow { width: auto; height: auto; background: none; -webkit-mask: none; mask: none; font-size: 24px; line-height: 1; transform: translateY(1px); filter: none; }
.merch-overlay.vcr-price .arrow::before { content: '\\25B2'; }
.merch-overlay.vcr-price .quote.down .arrow { transform: translateY(1px); }
.merch-overlay.vcr-price .quote.down .arrow::before { content: '\\25BC'; }
.merch-overlay.no-arrow-glow .arrow { text-shadow: none; }
.merch-overlay .quote.has-pct { position: relative; }
.merch-overlay .pct { position: absolute; top: 100%; right: 0; margin-top: 6px; font-size: 15px; line-height: 1; white-space: nowrap; }
.merch-overlay .name-wrap { flex: 1 1 auto; min-width: 0; display: grid; }
.merch-overlay .name-wrap > .name, .merch-overlay .name-wrap > .variants { grid-area: 1 / 1; }
.merch-overlay .name-wrap > .shop-label { grid-area: 2 / 1; }
.merch-overlay .shop-label { justify-self: start; margin-top: 6px; font-weight: var(--merch-name-weight, 700); font-size: 20px; line-height: 1.15; letter-spacing: 0.05em; text-transform: uppercase; color: var(--merch-shop-label-color, #3ddc5a); font-family: var(--merch-name-font); overflow-wrap: anywhere; text-shadow: var(--merch-text-glow, none); }
.merch-overlay .cta-footer { text-align: center; font-weight: var(--merch-name-weight, 700); font-size: 20px; line-height: 1.15; letter-spacing: 0.05em; text-transform: uppercase; color: var(--merch-cta-color, #3ddc5a); font-family: var(--merch-name-font); overflow-wrap: anywhere; text-shadow: var(--merch-text-glow, none); }
.merch-overlay .variants { display: flex; flex-wrap: wrap; align-content: center; align-items: center; gap: 0.5em; font-size: var(--variant-fs, 20px); opacity: 0; pointer-events: none; }
.merch-overlay .variant-box { display: inline-flex; align-items: center; justify-content: center; min-width: 1.55em; max-width: 100%; padding: 0.2em 0.5em; border: 2px solid var(--merch-name-color); color: var(--merch-name-color); font-weight: var(--merch-name-weight, 700); font-size: var(--variant-fs, 20px); line-height: 1.15; letter-spacing: 0.05em; text-transform: uppercase; font-family: var(--merch-name-font); text-decoration: none; overflow-wrap: anywhere; }
.merch-overlay .variant-box:hover, .merch-overlay .variant-box:focus-visible { background: rgba(255, 255, 255, 0.12); }
.merch-overlay .row.has-variants:hover .name-wrap > .name, .merch-overlay .row.has-variants:focus-within .name-wrap > .name { opacity: 0; pointer-events: none; }
.merch-overlay .row.has-variants:hover .variants, .merch-overlay .row.has-variants:focus-within .variants { opacity: 1; pointer-events: auto; }
.merch-overlay.fill-host { position: relative; width: 100%; height: 100%; }
.merch-overlay.fill-host .ticker { top: 0; right: 0; }
.merch-overlay.natural { position: relative; width: calc(var(--merch-wrap-width, ${MERCH_BOARD_NATURAL_WIDTH}px) + var(--square-delta, 0px) + var(--pad-delta, 0px) + 2 * var(--merch-outer-border-w, 0px)); height: auto; overflow: visible; }
.merch-overlay.natural .ticker { position: relative; top: 0; right: 0; }
`

const EXTUI_CSS = `
.merch-overlay .extui-controls { position: absolute; inset: 0; pointer-events: none; opacity: 1; }
.merch-overlay .extui-btn { pointer-events: none; position: absolute; width: 28px; height: 28px; padding: 0; border: 0; background: transparent; color: #f4f4f4; font-family: ${MERCH_BASE_FONT_STACK}; font-size: 18px; line-height: 1; text-shadow: 0 1px 6px rgba(0, 0, 0, 0.8); }
.merch-overlay .extui-collapse { top: 2px; right: 2px; width: max(28px, calc(var(--extui-collapse-x-size, 28px) + 12px)); height: max(28px, calc(var(--extui-collapse-x-size, 28px) + 12px)); font-size: var(--extui-collapse-x-size, 28px); color: var(--extui-collapse-x-color, #f4f4f4); text-shadow: 0 0.055em 0.33em rgba(0, 0, 0, 0.8), 0 0 0.12em rgba(0, 0, 0, 0.9); }
.merch-overlay .extui-plus { right: 2px; bottom: 2px; }
.merch-overlay .extui-minus { right: 30px; bottom: 2px; }
.merch-overlay .ticker.extui-collapsed { display: none; }
.merch-overlay .extui-tab { position: fixed; right: 0; bottom: min(var(--extui-tab-bottom, 150px), 15vh); z-index: 0; writing-mode: vertical-rl; padding-block: clamp(11.9px, 1.4vh, 14px); padding-inline: clamp(8.5px, 1vh, 10px); border: 0; background: var(--extui-tab-outline, #3ddc5a); clip-path: polygon(0 0, 100% 0, 100% 100%, 12px 100%, 0 calc(100% - 12px)); color: var(--extui-tab-text, #888886); font-family: var(--extui-tab-font, inherit); font-weight: 400; font-size: clamp(11.9px, 1.4vh, 14px); letter-spacing: 0.32em; line-height: 1; pointer-events: none; }
.merch-overlay .extui-tab::before { content: ''; position: absolute; inset: 0; z-index: -1; background: #000; clip-path: polygon(2px 2px, 100% 2px, 100% calc(100% - 2px), 12.83px calc(100% - 2px), 2px calc(100% - 12.83px)); }
.merch-overlay .extui-tab-arrow { display: inline-block; writing-mode: horizontal-tb; transform: rotate(45deg); }
.merch-overlay .extui-offsite { position: absolute; right: 9px; bottom: 9px; width: 9px; height: 9px; color: var(--extui-offsite-color, var(--merch-name-color, #fff)); opacity: var(--extui-offsite-opacity, 0.35); pointer-events: none; }
.merch-overlay .variant-box { position: relative; }
.merch-overlay .variant-box > .extui-offsite { right: 0.06em; bottom: 0.05em; width: 0.38em; height: 0.38em; }
`

const ExtUiOffsiteMark = (): JSX.Element => (
  <svg className='extui-offsite' viewBox='0 0 10 10' aria-hidden='true' focusable='false'>
    <path
      d='M2.2 7.8 7.8 2.2M3.9 2.2h3.9v3.9'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export type MerchExtUiPreview = 'expanded' | 'collapsed'

const EXTUI_TAB_FONT_STACKS: Record<string, string> = {
  DOTO: "'Doto', monospace",
  BITCOUNT_GRID_SINGLE: "'Bitcount Grid Single', monospace",
  VCR_OSD_MONO: "'VCR OSD Mono', monospace"
}

const MerchRow = ({
  row,
  cartBase,
  variantsEnabled,
  shopMode,
  shopLabel,
  offsiteMarks,
  rowRef
}: {
  row: MerchBoardPanelRow
  cartBase: string | null
  variantsEnabled: boolean
  shopMode: boolean
  shopLabel: string
  offsiteMarks: boolean
  rowRef?: (el: HTMLElement | null) => void
}): JSX.Element => {
  const boxesShown = variantsEnabled && cartBase !== null && row.variants.length >= 2
  const pctLabel = row.pctLabel ?? null
  const showPct = pctLabel !== null && pctLabel !== ''
  const imageUrl = httpsUrlOrNull(row.imageUrl)
  const linkUrl = httpsUrlOrNull(row.linkUrl)
  const variantBoxes = boxesShown && (
    <div className='variants'>
      {row.variants.map((v) => {
        const boxHref = httpsUrlOrNull(
          typeof v.linkUrl === 'string' && v.linkUrl !== '' ? v.linkUrl : `${cartBase}${v.id}:1`
        )
        return (
          <a
            key={v.id}
            className='variant-box'
            {...(boxHref !== null
              ? { href: boxHref, target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {v.label}
            {offsiteMarks && boxHref !== null && <ExtUiOffsiteMark />}
          </a>
        )
      })}
    </div>
  )
  const inner = (
    <>
      <div className='tile'>
        {imageUrl !== null
          ? <img src={imageUrl} alt={row.title} />
          : <span className='tile-initials'>{initials(row.title)}</span>}
      </div>
      <div className='row-main'>
        {shopMode
          ? (
            <div className='name-wrap'>
              <div className='name'>{row.title}</div>
              {variantBoxes}
              {shopLabel !== '' && <div className='shop-label'>{shopLabel}</div>}
            </div>
            )
          : (boxesShown
              ? (
                <div className='name-wrap'>
                  <div className='name'>{row.title}</div>
                  {variantBoxes}
                </div>
                )
              : <div className='name'>{row.title}</div>)}
        {!shopMode && (
          <div className={showPct ? `${quoteClass(row.trend)} has-pct` : quoteClass(row.trend)}>
            {row.trend !== null && (
              <span className='arrow' aria-hidden='true' />
            )}
            {row.priceCents !== null && <span className='price'>{money(row.priceCents)}</span>}
            {showPct && <span className='pct'>{pctLabel}</span>}
          </div>
        )}
      </div>
    </>
  )
  const rowHref = boxesShown ? null : linkUrl
  if (rowHref !== null) {
    return (
      <a
        ref={rowRef}
        className='row'
        href={rowHref}
        target='_blank'
        rel='noopener noreferrer'
      >
        {inner}
        {offsiteMarks && <ExtUiOffsiteMark />}
      </a>
    )
  }
  return (
    <div ref={rowRef} className={boxesShown ? 'row has-variants' : 'row'}>
      {inner}
    </div>
  )
}

export const MerchBoardPanel = ({
  storeTitle,
  rows,
  style,
  cartBase,
  shopMode,
  shopLabel,
  ctaText,
  fillHost,
  natural,
  wrapWidth,
  tickerRef,
  rowRef,
  extui
}: {
  storeTitle: string
  rows: MerchBoardPanelRow[]
  style: ResolvedMerchStyle
  cartBase?: string | null
  shopMode?: boolean
  shopLabel?: string | null
  ctaText?: string | null
  fillHost?: boolean
  natural?: boolean
  wrapWidth?: number
  tickerRef?: React.Ref<HTMLDivElement>
  rowRef?: (productId: string, el: HTMLElement | null) => void
  extui?: MerchExtUiPreview
}): JSX.Element => {
  const styleVars: Record<string, string> = {
    '--merch-title-color': style.titleColor,
    '--merch-title-font': merchFontStack(style.titleFontKey),
    '--merch-name-color': style.productTitleColor,
    '--merch-name-font': merchFontStack(style.productTitleFontKey),
    '--merch-price-font': merchFontStack(style.priceFontKey),
    '--merch-board-bg': style.boardBgColor,
    '--merch-row-bg': style.rowBgColor,
    '--merch-tile-bg': style.tileBgColor,
    '--merch-outer-bg': style.outerBgColor,
    '--merch-outer-radius': `${style.outerCornerRadius}px`,
    '--merch-board-radius': `${style.boardCornerRadius}px`,
    '--merch-outer-border-color': style.outerBorderColor,
    '--merch-outer-border-w': `${style.outerBorderWidth}px`,
    '--merch-title-weight': String(merchFontWeight(style.titleFontKey, 'title')),
    '--merch-name-weight': String(merchFontWeight(style.productTitleFontKey, 'name')),
    '--merch-price-weight': String(merchFontWeight(style.priceFontKey, 'price')),
    '--merch-wrap-width': `${wrapWidth ?? MERCH_BOARD_NATURAL_WIDTH}px`,
    '--merch-outer-pad-x': `${style.outerPadX}px`,
    '--merch-board-pad-x': `${style.boardPadX}px`
  }
  if (style.textGlowPx > 0) {
    styleVars['--merch-text-glow'] = `0 0 ${style.textGlowPx}px ${style.textGlowColor}`
    styleVars['--merch-title-glow'] = `${MERCH_TITLE_BASE_SHADOW}, 0 0 ${style.textGlowPx}px ${style.textGlowColor}`
    if (style.textGlowArrows) {
      styleVars['--merch-arrow-glow'] = `drop-shadow(0 0 ${style.textGlowPx}px ${style.textGlowColor})`
    }
  }
  const shopModeOn = shopMode === true
  const shopLabelText = shopModeOn && typeof shopLabel === 'string' ? shopLabel : ''
  const ctaShown = typeof ctaText === 'string' && ctaText !== ''
  if (style.rowBorderColor !== MERCH_STYLE_DEFAULTS.rowBorderColor) {
    styleVars['--merch-row-border'] = style.rowBorderColor
  }
  if (style.outerPadTop !== MERCH_STYLE_DEFAULTS.outerPadTop) {
    styleVars['--merch-outer-pad-top'] = `${style.outerPadTop}px`
  }
  if (style.outerPadBottom !== MERCH_STYLE_DEFAULTS.outerPadBottom) {
    styleVars['--merch-outer-pad-bottom'] = `${style.outerPadBottom}px`
  }
  if (shopModeOn) styleVars['--merch-shop-label-color'] = style.shopLabelColor
  if (ctaShown) styleVars['--merch-cta-color'] = style.ctaColor
  if (style.productImageHeight !== null) {
    styleVars['--merch-photo-h'] = `${style.productImageHeight}px`
  }
  const bgImageUrl = cssBgUrlOrNull(style.boardBgImage === '' ? null : style.boardBgImage)
  if (bgImageUrl !== null) {
    styleVars['--merch-board-bg-image'] = `url("${bgImageUrl}")`
  }

  const rootRef = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const root = rootRef.current
    if (root === null) return
    const refit = (): void => fitVariantStrips(root)
    refit()
    const frame = root.querySelector('.ticker')
    const ro = typeof ResizeObserver === 'undefined' || frame === null ? null : new ResizeObserver(refit)
    if (ro !== null && frame !== null) ro.observe(frame)
    const fonts = typeof document === 'undefined' ? undefined : document.fonts
    fonts?.addEventListener('loadingdone', refit)
    void fonts?.ready?.then(refit)
    return () => {
      ro?.disconnect()
      fonts?.removeEventListener('loadingdone', refit)
    }
  }, [rows, style, cartBase, wrapWidth])

  let rootClass = 'merch-overlay'
  if (natural === true) rootClass = 'merch-overlay natural'
  else if (fillHost === true) rootClass = 'merch-overlay fill-host'
  if (style.priceFontKey === 'VCR_OSD_MONO') rootClass += ' vcr-price'
  if (style.squarePhotos) rootClass += ' square-photos'
  if (style.textGlowPx > 0 && !style.textGlowArrows) rootClass += ' no-arrow-glow'
  if (style.productImageHeight !== null) rootClass += ' photo-height'
  if (bgImageUrl !== null) rootClass += ' board-bg-image'

  const extuiControlsVars: Record<string, string> = {
    '--extui-collapse-x-size': `${style.collapseXSize}px`
  }
  if (style.collapseXColor !== '') extuiControlsVars['--extui-collapse-x-color'] = style.collapseXColor
  const extuiTabVars: Record<string, string> = {
    '--extui-tab-outline': style.tabOutlineColor,
    '--extui-tab-text': style.tabTextColor,
    '--extui-tab-bottom': '150px'
  }
  const extuiTabFont = EXTUI_TAB_FONT_STACKS[style.titleFontKey]
  if (extuiTabFont !== undefined) extuiTabVars['--extui-tab-font'] = extuiTabFont
  if (extui !== undefined && style.offsiteMarkColor !== '') {
    styleVars['--extui-offsite-color'] = style.offsiteMarkColor
    styleVars['--extui-offsite-opacity'] = '1'
  }

  return (
    <div
      ref={rootRef}
      className={rootClass}
      style={styleVars as React.CSSProperties}
      data-testid='merch-overlay-view'
    >
      <style>{TICKER_CSS}</style>
      {extui !== undefined && <style>{EXTUI_CSS}</style>}
      <div className={extui === 'collapsed' ? 'ticker extui-collapsed' : 'ticker'} ref={tickerRef}>
        <div className='store-title'>{storeTitle}</div>
        <div className='board' data-testid='merch-board'>
          {rows.map((row) => (
            <MerchRow
              key={row.productId}
              row={row}
              cartBase={cartBase ?? null}
              variantsEnabled={style.showVariants}
              shopMode={shopModeOn}
              shopLabel={shopLabelText}
              offsiteMarks={extui !== undefined}
              rowRef={rowRef === undefined ? undefined : (el) => rowRef(row.productId, el)}
            />
          ))}
          {ctaShown && <div className='cta-footer'>{ctaText}</div>}
        </div>
        {extui === 'expanded' && (
          <div className='extui-controls' aria-hidden='true' style={extuiControlsVars as React.CSSProperties}>
            <button type='button' tabIndex={-1} className='extui-btn extui-collapse'>✕</button>
            <button type='button' tabIndex={-1} className='extui-btn extui-minus'>−</button>
            <button type='button' tabIndex={-1} className='extui-btn extui-plus'>+</button>
          </div>
        )}
      </div>
      {extui === 'collapsed' && (
        <button type='button' tabIndex={-1} aria-hidden='true' className='extui-tab' style={extuiTabVars as React.CSSProperties}>
          MERCH<span className='extui-tab-arrow'>↑</span>
        </button>
      )}
    </div>
  )
}
